import React, { useState } from "react";
import { Modal, Button, Table, Badge, Row, Col, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DocumentFolder, GroupedError } from "../../models/Document";
import { formatDate } from "../../utils/dateUtils";
import logger from "../../utils/logging";

interface DocumentFolderDetailsModalProps {
    show: boolean;
    onClose: () => void;
    folder: DocumentFolder | null;
    documentErrorsProvider: (folder: DocumentFolder) => Promise<GroupedError[]>;
    folderDeleteHandler: (folder: DocumentFolder) => void;
}

const DocumentFolderDetailsModal: React.FC<DocumentFolderDetailsModalProps> = ({
    show,
    onClose,
    folder,
    documentErrorsProvider,
    folderDeleteHandler,
}) => {
    const { t } = useTranslation(["documents", "common"]);
    const [errors, setErrors] = useState<GroupedError[]>([]);
    const [showErrors, setShowErrors] = useState(false);
    const [loadingErrors, setLoadingErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    if (!folder) {
        return null;
    }

    const handleShowErrors = async () => {
        if (showErrors) {
            setShowErrors(false);
            return;
        }

        setLoadingErrors(true);
        setErrorMessage(null);

        try {
            const groupedErrors = await documentErrorsProvider(folder);
            setErrors(groupedErrors);
            setShowErrors(true);
        } catch (error) {
            logger.error("Failed to fetch document errors:", error);
            setErrorMessage(t("detailsModal.processingErrors.loadError"));
        } finally {
            setLoadingErrors(false);
        }
    };

    const hasErrors: boolean = folder.failureCount != undefined && folder.failureCount > 0;

    const translateDeletionRule = (deletionRule: string): string => {
        return t(`common:deletionRule.ruleOptions.${deletionRule}`);
    };

    const getProcessingStatusBadge = (status?: string) => {
        if (!status) return <Badge bg="secondary">{t("processingStates.UNKNOWN")}</Badge>;

        let variant: string;
        switch (status) {
            case "FINISHED":
                variant = "success";
                break;
            case "FAILED":
                variant = "danger";
                break;
            case "COMPLETED_WITH_ERRORS":
                variant = "warning";
                break;
            case "RUNNING":
                variant = "info";
                break;
            default:
                variant = "secondary";
        }

        return <Badge bg={variant}>{t(`common:processingStates.${status}` as any, { defaultValue: status })}</Badge>;
    };

    const getProcessingSummary = () => {
        if (folder.totalCount === undefined) {
            return t("common:processingStates.UNKNOWN");
        }

        const successCount = folder.successCount || 0;
        const failureCount = folder.failureCount || 0;
        const skippedCount = folder.skippedCount || 0;
        const processedCount = successCount + failureCount + skippedCount;

        if (processedCount === 0) {
            return `0/${folder.totalCount} (${t("common:processingStates.RUNNING")})`;
        }

        return `${processedCount}/${folder.totalCount} (${successCount} ✓${
            failureCount > 0 ? `, ${failureCount} ✗` : ""
        }${skippedCount > 0 ? `, ${skippedCount} ⏭` : ""})`;
    };

    const handleDeleteClick = () => {
        if (folderDeleteHandler && folder) {
            folderDeleteHandler(folder);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t("detailsModal.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={6}>
                        <Table>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.batchId")}</strong>
                                    </td>
                                    <td>
                                        <code className="text-muted small">{folder.id}</code>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.name")}</strong>
                                    </td>
                                    <td>{folder.name || <em className="text-muted">{t("detailsModal.noName")}</em>}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.createdAt")}</strong>
                                    </td>
                                    <td>{formatDate(folder.createdAt)}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.updatedAt")}</strong>
                                    </td>
                                    <td>
                                        {folder.updatedAt ? (
                                            formatDate(folder.updatedAt)
                                        ) : (
                                            <em className="text-muted">{t("common:notAvailable")}</em>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.processingStatus")}</strong>
                                    </td>
                                    <td>{getProcessingStatusBadge(folder.processingStatus)}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                    <Col md={6}>
                        <Table>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.totalDocuments")}</strong>
                                    </td>
                                    <td>
                                        {folder.totalCount !== undefined ? (
                                            folder.totalCount
                                        ) : (
                                            <em className="text-muted">{t("common:notAvailable")}</em>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.successCount")}</strong>
                                    </td>
                                    <td>
                                        {folder.successCount !== undefined ? (
                                            <span className="text-success">{folder.successCount} ✓</span>
                                        ) : (
                                            <em className="text-muted">{t("common:notAvailable")}</em>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.failureCount")}</strong>
                                    </td>
                                    <td>
                                        {folder.failureCount !== undefined ? (
                                            <span className={folder.failureCount > 0 ? "text-danger" : "text-muted"}>
                                                {folder.failureCount} {folder.failureCount > 0 ? "✗" : ""}
                                            </span>
                                        ) : (
                                            <em className="text-muted">{t("common:notAvailable")}</em>
                                        )}
                                    </td>
                                </tr>
                                {folder.skippedCount !== undefined && folder.skippedCount > 0 && (
                                    <tr>
                                        <td>
                                            <strong>{t("detailsModal.fields.skippedCount")}</strong>
                                        </td>
                                        <td>
                                            <span className="text-muted">{folder.skippedCount} ⏭</span>
                                        </td>
                                    </tr>
                                )}
                                <tr>
                                    <td>
                                        <strong>{t("detailsModal.fields.progressSummary")}</strong>
                                    </td>
                                    <td>{getProcessingSummary()}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                </Row>
                <hr />
                {hasErrors && (
                    <>
                        <Row className="mb-3">
                            <Col>
                                <h5>{t("detailsModal.processingErrors.title")}</h5>
                                <p className="text-muted">
                                    {t("detailsModal.processingErrors.description", {
                                        count: folder.failureCount,
                                        context: folder.failureCount === 1 ? "singular" : "plural",
                                    })}
                                </p>
                                <Button
                                    variant={showErrors ? "secondary" : "outline-danger"}
                                    size="sm"
                                    onClick={handleShowErrors}
                                    disabled={loadingErrors}
                                >
                                    {loadingErrors ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            {t("common:loading")}
                                        </>
                                    ) : showErrors ? (
                                        t("detailsModal.processingErrors.hideDetails")
                                    ) : (
                                        t("detailsModal.processingErrors.viewDetails")
                                    )}
                                </Button>
                            </Col>
                        </Row>

                        {errorMessage && (
                            <Alert variant="danger" dismissible onClose={() => setErrorMessage(null)}>
                                {errorMessage}
                            </Alert>
                        )}

                        {showErrors && (
                            <Row className="mb-3">
                                <Col>
                                    {errors.length === 0 ? (
                                        <Alert variant="info">{t("detailsModal.processingErrors.noDetails")}</Alert>
                                    ) : (
                                        <Table striped bordered hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t("detailsModal.processingErrors.errorMessage")}</th>
                                                    <th>{t("detailsModal.processingErrors.affectedGuests")}</th>
                                                    <th>{t("detailsModal.processingErrors.errorCode")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {errors.map((error, index) => (
                                                    <tr key={index}>
                                                        <td>{error.error}</td>
                                                        <td>
                                                            <Badge bg="danger">{error.count}</Badge>
                                                            {" " +
                                                                t("detailsModal.processingErrors.guests", {
                                                                    count: error.count,
                                                                    context: error.count === 1 ? "singular" : "plural",
                                                                })}
                                                        </td>
                                                        <td>
                                                            <code className="text-muted">{error.errorCode}</code>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </Col>
                            </Row>
                        )}

                        <hr />
                    </>
                )}
                <h5>{t("detailsModal.configurationDetails")}</h5>
                <Table>
                    <tbody>
                        <tr>
                            <td>
                                <strong>{t("detailsModal.fields.guestFilter")}</strong>
                            </td>
                            <td>
                                {t(`common:guestFilters.${folder.outputSettings.guestFilter}` as any, {
                                    defaultValue: folder.outputSettings.guestFilter,
                                })}
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <strong>{t("detailsModal.fields.documentNaming")}</strong>
                            </td>
                            <td>
                                {t(`common:documentNaming.${folder.outputSettings.documentNaming}` as any, {
                                    defaultValue: folder.outputSettings.documentNaming,
                                })}
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <strong>{t("detailsModal.fields.deletionRule")}</strong>
                            </td>
                            <td>{translateDeletionRule(folder.outputSettings.deletionRule)}</td>
                        </tr>
                        <tr>
                            <td>
                                <strong>{t("detailsModal.fields.folderUrl")}</strong>
                            </td>
                            <td>
                                <a href={folder.url} target="_blank" rel="noopener noreferrer" className="text-break">
                                    {folder.url}
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <Button variant="danger" onClick={handleDeleteClick}>
                        {t("common:buttons.delete")}
                    </Button>
                    <div className="ms-auto">
                        <Button variant="secondary" onClick={onClose}>
                            {t("common:buttons.close")}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default DocumentFolderDetailsModal;
